import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Tab from '@material-ui/core/Tab';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from 'components/Common/MUI/TabPanel/TabPanel';
import { tabContext } from 'contexts/tabContext';
import ProductDescription from './ProductDescription/ProductDescription';
import ProductHelp from './ProductHelp/ProductHelp';
import ProductRSE from './ProductRSE/ProductRSE';
import ProductReviews from './ProductReviews/ProductReviews';
import ProductShipping from './ProductShipping/ProductShipping';
import ProductSizeGuide from './ProductSizeGuide/ProductSizeGuide';
import { CustomTab } from './UI/CustomTab/CustomTab';
import { useSuspenseListener } from 'hooks/useSuspenseListener';
import { isBot } from 'services/ua';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';
import {
  ProductReviewsDetailsType,
  ProductReviewsRatingType,
} from 'types/ProductPageV2/ProductReviews';
import {
  DESCRIPTION_TAB_ID,
  HELP_TAB_ID,
  RATING_TAB_ID,
  RSE_TAB_ID,
  SHIPPING_TAB_ID,
  SIZE_GUIDE_TAB_ID,
} from 'settings/productV2';

import styles from './ProductTabDesktop.module.scss';

/**
 * Product page of website. Contains details product data.
 */
function ProductTabDesktop({
  productAttributes,
  rating,
  detailsReviews,
}: {
  productAttributes: SelectedProductAttributes;
  rating: ProductReviewsRatingType | undefined;
  detailsReviews: ProductReviewsDetailsType[] | undefined;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(true);
  const [collapsible, setCollapsible] = useState(false);
  const [maxHeight, setMaxHeight] = useState(600);
  const { idTab, setIdTab, tabsRef } = useContext(tabContext);
  const { t } = useTranslation();
  const { isSuspending } = useSuspenseListener();
  const { sizeGuide } = productAttributes;
  const isUa = isBot();

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number
  ) => {
    setIdTab(newValue);
  };

  const collapsiblePanel = useRef<HTMLDivElement>(null);

  const refs = useRef<HTMLDivElement[]>([]);

  /**
   * Get ID Tab
   *
   * @return number
   */
  const getIdTab = () => idTab ?? 0;

  const scrollToTop = () => {
    collapsiblePanel.current?.scroll({
      top: 0,
    });
  };

  const handleContentHeight = () => {
    const elHeight = refs.current[getIdTab()]?.clientHeight;
    const descriptionTab = getIdTab() == DESCRIPTION_TAB_ID;
    descriptionTab ? setCollapsed(false) : setCollapsed(true);
    setCollapsible(elHeight > 300);
    setMaxHeight(elHeight);
  };

  useEffect(() => {
    if (!isSuspending) {
      handleContentHeight();
      setCollapsed(false);
    }
  }, [isSuspending]);

  /**
   * Toggle class when transition is ended on collapsible element
   */
  const collapsibleTransitionEndHandler = (
    event: React.TransitionEvent<HTMLDivElement>
  ) => {
    (event.target as HTMLDivElement).classList[!collapsed ? 'add' : 'remove'](
      styles.allowScrollbar
    );
  };

  const sections = [
    {
      styleRoot: styles.descriptionPanel,
      id: DESCRIPTION_TAB_ID,
      label: t('LABEL_TAB_DESCRIPTION'),
      gtm_label: t('LABEL_TAB_DESCRIPTION', { lng: 'fr' }),
      content: (
        <ProductDescription
          productAttributes={productAttributes}
          handleContentHeight={handleContentHeight}
        />
      ),
    },
    {
      id: SIZE_GUIDE_TAB_ID,
      label: t('LABEL_TAB_SIZE_GUIDE'),
      gtm_label: t('LABEL_TAB_SIZE_GUIDE', { lng: 'fr' }),
      content: (
        <ProductSizeGuide
          sizeGuide={sizeGuide.Value}
          handleContentHeight={handleContentHeight}
        />
      ),
    },
    {
      id: SHIPPING_TAB_ID,
      label: t('LABEL_TAB_SHIPPING'),
      gtm_label: t('LABEL_TAB_SHIPPING', { lng: 'fr' }),
      content: <ProductShipping handleContentHeight={handleContentHeight} />,
    },
    {
      id: RATING_TAB_ID,
      label: t('LABEL_TAB_RATING'),
      gtm_label: t('LABEL_TAB_RATING', { lng: 'fr' }),
      content: (
        <ProductReviews
          rating={rating}
          detailsReviews={detailsReviews}
          handleContentHeight={handleContentHeight}
        />
      ),
    },
    {
      id: RSE_TAB_ID,
      label: t('LABEL_TAB_RSE'),
      gtm_label: t('LABEL_TAB_RSE', { lng: 'fr' }),
      content: (
        <ProductRSE
          htmlContent={productAttributes?.RSE?.Value}
          handleContentHeight={handleContentHeight}
        />
      ),
    },
    {
      id: HELP_TAB_ID,
      label: t('LABEL_TAB_HELP'),
      gtm_label: t('LABEL_TAB_HELP', { lng: 'fr' }),
      content: <ProductHelp handleContentHeight={handleContentHeight} />,
    },
  ];

  const renderTab = (label: string, gtm_label: string, id: number) => {
    if (id !== HELP_TAB_ID) {
      return (
        <CustomTab
          label={label}
          gtm_label={gtm_label}
          value={id.toString()}
          key={id}
          className={classNames('MuiButtonBase-root', styles.tabItem)}
          selectedClassName={classNames('Mui-selected', styles.tabItemSelected)}
        />
      );
    }

    return (
      <Tab
        disableRipple
        label={label}
        value={id.toString()}
        key={id}
        classes={{ root: styles.tabItem, selected: styles.tabItemSelected }}
      />
    );
  };

  return (
    <>
      <TabContext value={getIdTab().toString()}>
        <div ref={tabsRef as React.RefObject<HTMLDivElement>} />
        <div className={styles.tabsContainer}>
          <TabList
            onChange={handleChange}
            aria-label="product info tabs"
            classes={{
              root: styles.tabListRoot,
              flexContainer: styles.tabListFlexContainer,
              fixed: styles.tabListFixed,
              indicator: styles.tabListIndicator,
            }}
          >
            {sections.map(section => {
              const { id, label, gtm_label } = section;
              return renderTab(label, gtm_label, id);
            })}
          </TabList>
          <div
            className={classNames(
              collapsible ? styles.tabPanelsContainer : '',
              !collapsed ? styles.openTabPanelsContainer : ''
            )}
          >
            <div
              className={classNames(collapsible ? styles.collapsible : '')}
              onTransitionEnd={collapsibleTransitionEndHandler}
              ref={collapsiblePanel}
              style={{
                height: collapsed
                  ? maxHeight < 300
                    ? maxHeight
                    : 300
                  : maxHeight < 600
                  ? maxHeight
                  : 600,
              }}
            >
              {sections.map(section => {
                const { id, content, styleRoot } = section;
                return (
                  <TabPanel
                    keepMounted={isUa}
                    classes={{
                      root: classNames(
                        'MuiTabPanel-root',
                        styles.tabPanelRoot,
                        styleRoot
                      ),
                    }}
                    value={id.toString()}
                    key={id}
                    ref={(element: HTMLDivElement) => {
                      refs.current[id] = element;
                    }}
                  >
                    {content}
                  </TabPanel>
                );
              })}
            </div>
            {collapsible && (
              <div className={styles.seeMoreBtnBlock}>
                <button
                  onClick={() => {
                    !collapsed && scrollToTop();
                    setCollapsed(!collapsed);
                  }}
                >
                  {collapsed ? t('LABEL_SHOW_MORE') : t('LABEL_SHOW_LESS')}
                </button>
              </div>
            )}
          </div>
        </div>
      </TabContext>
    </>
  );
}

export default ProductTabDesktop;
